import { useReducer, Reducer, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  getRostersUploadRequirements,
  getCourseCatalogUploadRequirements,
  getStudentDemographicsUploadRequirements,
  getStudentTranscriptsUploadRequirements,
  getTestScoresUploadRequirements,
  getCTEPathwaysUploadRequirements
} from '../../services/uploadRequirementsService';
import SchoolList from '../SchoolsList';
import SchoolTable from '../SchoolsTable';
import UploadCard from '../reusable/UploadCard';
import { getUploadResults, getUploadResultByFileId } from '../../services/uploadService';
import { UploadedFileData } from '../../interfaces/UploadedFileDataInterface';
import { fileTypes, UPLOAD_SECTIONS_IDS, NAVIGATION_LINKS_DATA, DATA_CATEGORY_LABELS } from '../../constants';
import UploadResultsContext from '../../context/UploadResultsContext';
import reducer, { DATA_UPLOAD_REDUCER_ACTIONS } from './dataUploadReducer';
import { Action, UploadResults } from '../../context/UploadResultsContext';
import PageHeader from '../reusable/PageHeader';
import WithDataWrapper from '../reusable/WithDataWrapper';
import AuditLogModalTable from './AuditLogModalTable';
import AuditLogTableContext from '../../context/AuditLogTableContext';
import FileTypesValues from '../../interfaces/FileType';
import WhatToUpload from '../reusable/WhatToUpload';
import DataUploadSidebar from './DataUploadSidebar';
import { SchoolsTableData } from '../SchoolsTable';
import { SchoolsListData } from '../SchoolsList';
import { calculateFlatDataUploadProgress, calculateNestedDataUploadProgress } from './utilityFunctions';
import useErrorModal from '../hooks/useErrorModal';
import PubNub from 'pubnub';
import useUserDetails from '../hooks/useUserDetails';
import useUploadProgress from '../hooks/useUploadProgress';
import type { UploadResultsContextValue } from '../../context/UploadResultsContext';

import './DataUpload.css';

interface DataUploadProps {
  data: UploadedFileData[]
}

interface AuditLogData {
  isOpen: boolean;
  fileType: FileTypesValues | ''
}

const PUBNUB_NETWORK_UP_CATEGORY_STATUS = 'PNNetworkUpCategory';
const pageDescription = 'Upload student rosters, course catalogs, and other EOAA data requirements.';

export const DataUpload = ({ data }: DataUploadProps) => {
  const { uploadRequirements, uploadProgressByCategory, refreshUploadProgress } = useUploadProgress();

  const [uploadResultsByCategory, dispatch] = useReducer<Reducer<UploadResults, Action>>(reducer, distributeData(data));
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [auditLogData, setAuditLogData] = useState<AuditLogData>({ isOpen: false, fileType: '' });

  const fileUploadDataById = useRef<UploadResultsContextValue['uploadData'] | {}>({});

  const { handleOpen } = useErrorModal();

  const { userDetails: { data: userDetails }, pubNubToken } = useUserDetails();


  const updateFileResult = async (eoaId: string, access_token: string, data_file_id: string) => {
    try {
      let response = await getUploadResultByFileId(eoaId, access_token, data_file_id);
      dispatch({ type: DATA_UPLOAD_REDUCER_ACTIONS.UPDATE_UPLOADED_FILES_DATA, payload: response.data });
    } catch (error) {
      handleOpen('Error occured while fetching upload results data!', error as Error);
    }
  };

  useEffect(() => {
    if (userDetails && pubNubToken) {
      const pubnub = new PubNub({
        subscribeKey: process.env.REACT_APP_PUBNUB_SUBSCRIBE_KEY,
        userId: userDetails.id
      });
      pubnub.setToken(pubNubToken);
  
      const channel = pubnub.channel(`eoa.${userDetails.eoaId}.filestatuschanged`);
      const subscription = channel.subscription();

      pubnub.addListener({
        status: (status) => {
          if (status.error) console.error(`PubNub error: ${status.category}`);
          if (status.category === PUBNUB_NETWORK_UP_CATEGORY_STATUS) subscription.subscribe();
        }
      });

      subscription.onMessage = (messageEvent: any) => {
        const data_file_id = messageEvent.message.data_file_id;
        updateFileResult(userDetails.eoaId, userDetails.access_token, data_file_id);
        refreshUploadProgress();
      };

      subscription.subscribe();

      return () => {
        pubnub.removeAllListeners();
        pubnub.unsubscribeAll();
      };
    }
  }, [userDetails, pubNubToken]);

  const selectedCardIndex = cardsData.findIndex(card => card.cardId === selectedCardId);

  const handleOpenAuditLog = (fileType: FileTypesValues | '') => {
    if (fileType === auditLogData.fileType) return;

    setAuditLogData({ isOpen: true, fileType });
  };

  const handleCloseSidebar = () => {
    setSelectedCardId(null);
  };

  return (
    <>
      <PageHeader title={NAVIGATION_LINKS_DATA.DATA_UPLOAD.label} pageDescription={pageDescription} />
      <div className="data-upload">
        <div className="main-content">
          <UploadResultsContext.Provider value={{ uploadResults: uploadResultsByCategory, dispatch, uploadData: fileUploadDataById.current }}>
            <AuditLogTableContext.Provider value={{ handleOpenAuditLog }}>
              {cardsData.map((cardData, index) => (
                <UploadCard
                  key={index}
                  title={cardData.cardTitle}
                  helperText={cardData.cardHelperText}
                  uploadedFilesData={uploadResultsByCategory[cardData.fileType]}
                  fileType={cardData.fileType}
                  cardId={cardData.cardId}
                  handleSelectedCardId={setSelectedCardId}
                  sampleFileName={cardData.sampleFileName}
                  uploadProgressData={uploadProgressByCategory[cardData.cardTitle]}
                />
              ))}
            </AuditLogTableContext.Provider>
          </UploadResultsContext.Provider>
          {auditLogData.isOpen ?
            <AuditLogModalTable handleCloseModal={() => setAuditLogData({ isOpen: false, fileType: '' })} fileType={auditLogData.fileType} />
            :
            null
          }
        </div>
        {createPortal(
          selectedCardId ?
            <DataUploadSidebar
              handleCloseSidebar={handleCloseSidebar}
            >
              <WhatToUpload
                cardTitle={cardsData[selectedCardIndex].cardTitle}
                fileTypeLabel={cardsData[selectedCardIndex].fileTypeLabel}
                handleCloseSidebar={handleCloseSidebar}
              >
                {cardsData[selectedCardIndex].displayTable ?
                  <SchoolTable data={uploadRequirements[selectedCardIndex] as SchoolsTableData} fileType={cardsData[selectedCardIndex].fileType} />
                  :
                  <SchoolList data={uploadRequirements[selectedCardIndex] as SchoolsListData} />
                }
              </WhatToUpload>
            </DataUploadSidebar>
            :
            <></>
          , document.getElementById('sideDrawer') as HTMLElement)
        }
      </div>
    </>
  );
};

const cardsData = [
  {
    cardTitle: DATA_CATEGORY_LABELS[fileTypes.STUDENT_ROSTER],
    cardHelperText: [
      'Upload student roster spreadsheet(s) for the 2023-2024 graduating class for all schools participating in the audit. If the file contains data from multiple schools, make sure each row contains a School ID and Student ID.'
    ],
    sampleFileName: 'Roster Sample File.csv',
    fileTypeLabel: 'student roster',
    displayTable: false,
    fetchData: getRostersUploadRequirements,
    fileType: fileTypes.STUDENT_ROSTER,
    cardId: UPLOAD_SECTIONS_IDS[fileTypes.STUDENT_ROSTER],
    calculateUploadProgress: calculateFlatDataUploadProgress,
    errorMessage: 'Error occured while fetching student roster data!'
  },
  {
    cardTitle: DATA_CATEGORY_LABELS[fileTypes.COURSE_CATALOG],
    cardHelperText: [
      'Upload course catalog spreadsheet(s) for the four years leading up to and including the 2023-2024 school year for all schools participating in the audit. If the file contains data from multiple schools, make sure each row contains a School ID and Student ID.'
    ],
    sampleFileName: 'Course catalog Sample File.csv',
    fileTypeLabel: 'course catalog',
    displayTable: true,
    fetchData: getCourseCatalogUploadRequirements,
    fileType: fileTypes.COURSE_CATALOG,
    cardId: UPLOAD_SECTIONS_IDS[fileTypes.COURSE_CATALOG],
    calculateUploadProgress: calculateNestedDataUploadProgress,
    errorMessage: 'Error occured while fetching course catalog data!'
  },
  {
    cardTitle: DATA_CATEGORY_LABELS[fileTypes.STUDENT_DEMOGRAPHICS],
    cardHelperText: [
      'Upload student demographics spreadsheet(s) for the 2023-2024 graduating class for all schools participating in the audit. If the file contains data from multiple schools, make sure each row contains a School ID and Student ID.'
    ],
    sampleFileName: 'Student demographics Sample File.csv',
    fileTypeLabel: 'student demographic',
    displayTable: true,
    fetchData: getStudentDemographicsUploadRequirements,
    fileType: fileTypes.STUDENT_DEMOGRAPHICS,
    cardId: UPLOAD_SECTIONS_IDS[fileTypes.STUDENT_DEMOGRAPHICS],
    calculateUploadProgress: calculateNestedDataUploadProgress,
    errorMessage: 'Error occured while fetching student demographics data!'
  },
  {
    cardTitle: DATA_CATEGORY_LABELS[fileTypes.STUDENT_TRANSCRIPTS],
    cardHelperText: [
      'Upload student transcript spreadsheet(s) for the 2023-2024 graduating class for all schools participating in the audit.'
    ],
    sampleFileName: 'Transcripts Sample File.csv',
    fileTypeLabel: 'student transcript',
    displayTable: false,
    fetchData: getStudentTranscriptsUploadRequirements,
    fileType: fileTypes.STUDENT_TRANSCRIPTS,
    cardId: UPLOAD_SECTIONS_IDS[fileTypes.STUDENT_TRANSCRIPTS],
    calculateUploadProgress: calculateFlatDataUploadProgress,
    errorMessage: 'Error occured while fetching student transcripts data!'
  },
  {
    cardTitle: DATA_CATEGORY_LABELS[fileTypes.TEST_SCORES],
    cardHelperText: [
      'Upload test score spreadsheet(s) for the 2023-2024 graduating class for all schools participating in the audit.'
    ],
    sampleFileName: 'Test scores Sample File.csv',
    fileTypeLabel: 'test score',
    displayTable: true,
    fetchData: getTestScoresUploadRequirements,
    fileType: fileTypes.TEST_SCORES,
    cardId: UPLOAD_SECTIONS_IDS[fileTypes.TEST_SCORES],
    calculateUploadProgress: calculateNestedDataUploadProgress,
    errorMessage: 'Error occured while fetching test scores data!'
  },
  {
    cardTitle: DATA_CATEGORY_LABELS[fileTypes.CTE_PATHWAYS],
    cardHelperText: [
      'Upload CTE pathways for the 2023-2024 graduating class for all schools participating in the audit.'
    ],
    sampleFileName: 'CTE pathways Sample File.xlsx',
    fileTypeLabel: 'CTE pathway',
    displayTable: true,
    fetchData: getCTEPathwaysUploadRequirements,
    fileType: fileTypes.CTE_PATHWAYS,
    cardId: UPLOAD_SECTIONS_IDS[fileTypes.CTE_PATHWAYS],
    calculateUploadProgress: calculateNestedDataUploadProgress,
    errorMessage: 'Error occured while fetching CTE pathways data!'
  }
];

const distributeData = (data: UploadedFileData[]) => {
  let initialState: UploadResults = {
    [fileTypes.STUDENT_ROSTER]: [],
    [fileTypes.COURSE_CATALOG]: [],
    [fileTypes.STUDENT_DEMOGRAPHICS]: [],
    [fileTypes.STUDENT_TRANSCRIPTS]: [],
    [fileTypes.TEST_SCORES]: [],
    [fileTypes.CTE_PATHWAYS]: []
  };

  data.forEach((uploadResult: UploadedFileData) => {
    if (Object.keys(initialState).includes(uploadResult.upload_file_type)) {
      initialState[uploadResult.upload_file_type].push(uploadResult);
    }
  });

  return initialState;
};

export default function DataUploadwithData() {

  return <WithDataWrapper
    fetchData={getUploadResults}
    errorMessage="Error occurred while fetching upload data!"
    initialState={[]}
  >
    {(data) => <DataUpload data={data} />}
  </WithDataWrapper>;
};
